import {
  useMutation,
  useQueryClient,
  UseMutationResult,
  UseMutationOptions,
} from "@tanstack/react-query"
import { guardAppUsage } from "@mimber-client/guards/AppGuard"
import { useServerContext } from "@mimber-client/contexts/ServerContext"
import type {
  SuspendMemberDto,
  OrganizationMembershipDto,
  ResponseDto,
} from "@mimber/schema"
import { membersKeys } from "../members.utils"

interface SuspendMemberOptions extends Omit<
  UseMutationOptions<
    ResponseDto<OrganizationMembershipDto>,
    Error,
    SuspendMemberDto
  >,
  "mutationFn"
> {}

export const useSuspendMember = (
  memberUuid: string,
  options: SuspendMemberOptions = {}
): UseMutationResult<
  ResponseDto<OrganizationMembershipDto>,
  Error,
  SuspendMemberDto
> => {
  guardAppUsage("mimber-organization", "useSuspendMember")

  const { server } = useServerContext()
  const queryClient = useQueryClient()

  return useMutation({
    ...options,
    mutationFn: async (data: SuspendMemberDto) => {
      const response = await server.patch<
        ResponseDto<OrganizationMembershipDto>
      >(`members/${memberUuid}/suspend`, data)
      return response.data
    },
    onSuccess: (response, variables, onMutateResult, context) => {
      queryClient.invalidateQueries({ queryKey: membersKeys.findAll() })
      queryClient.invalidateQueries({
        queryKey: membersKeys.findOne(memberUuid),
      })
      options?.onSuccess?.(response, variables, onMutateResult, context)
    },
  })
}
